<template>
	<CtaModal
		:open="isOpen"
		:title="t('welcomeModal.title')"
		:message="t('welcomeModal.message')"
		:features="features"
		:confirm-label="t('welcomeModal.login')"
		:dismiss-label="t('welcomeModal.continueAsGuest')"
		@confirm="login"
		@dismiss="continueAsGuest"
	/>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { storeToRefs } from "pinia"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import CtaModal, { type CtaModalFeature } from "@/components/modals/CtaModal.vue"
import { IconName } from "@/components/icons/registry"
import { UseRootStore } from "@/model/RootStore"
import { config, Feature } from "@/AppConfig.ts"
import { Route } from "@/utils/routing"

const SESSION_KEY = "welcome-modal-shown"

const featureIcons = config.ui.has(Feature.ExtendedAccess)
	? [
			{ key: "realEstateData", icon: IconName.MapPin },
			{ key: "parcelAnalysis", icon: IconName.FileText },
			{ key: "investmentOpportunities", icon: IconName.Target },
		]
	: [
			{ key: "realEstateData", icon: IconName.MapPin },
			{ key: "exclusiveOpportunities", icon: IconName.Target },
			{ key: "savedSearches", icon: IconName.Bookmark },
		]

const store = UseRootStore()
const { isUserLoggedIn } = storeToRefs(store)
const router = useRouter()
const { t } = useI18n()

const features = computed<CtaModalFeature[]>(() =>
	featureIcons.map(feature => ({ ...feature, label: t(`welcomeModal.features.${feature.key}`) }))
)

const isOpen = ref(!isUserLoggedIn.value && sessionStorage.getItem(SESSION_KEY) !== "true")

function dismiss(): void {
	isOpen.value = false
	sessionStorage.setItem(SESSION_KEY, "true")
}

function login(): void {
	dismiss()
	router.push({ name: Route.Login })
}

function continueAsGuest(): void {
	dismiss()
}
</script>
